import { FC, useState } from "react";

import {
  FontSizes,
  IColumn,
  IRenderFunction,
  mergeStyles,
  noWrap,
  Pivot,
  PivotItem,
  Stack,
  Text,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import {
  ContactStatus,
  ContactType
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import {
  categoryColumn,
  nameColumn,
  phyAddressColumn
} from "@modules/practice/screens/address-book/components/contacts-lists/contact-list-columns.tsx";
import { IndividualAndOrgAddressColumn } from "@modules/practice/screens/address-book/components/contacts-lists/Individual-organisation-columns/IndividualAndOrganisationAddressColumn.tsx";
import { IndividualAndOrganisationEmailColumn } from "@modules/practice/screens/address-book/components/contacts-lists/Individual-organisation-columns/IndividualAndOrganisationEmailColumn.tsx";
import { IndividualOrOrganisationPhoneColumn } from "@modules/practice/screens/address-book/components/contacts-lists/Individual-organisation-columns/IndividualOrOrganisationPhoneColumn.tsx";
import { Contact } from "@stores/practice/models/Contact.ts";
import {
  DataFetcher,
  withFetch
} from "@ui-components/data-fetcher/DataFetcher.tsx";
import { ContactCategoryText } from "@ui-components/RefText.tsx";

import { AdvancedContactSearchList } from "./AdvancedContactSearchList.tsx";
import { AdvancedSearchListFilter } from "./AdvancedContactSearchListFilter.tsx";

type ExtractRenderFunctionProp<T> = T extends IRenderFunction<infer U>
  ? U
  : never;
type IDetailsColumnFieldProps = ExtractRenderFunctionProp<
  IColumn["onRenderField"]
>;
type DefaultRender = Parameters<IRenderFunction<IDetailsColumnFieldProps>>[1];

const CategoriesCell = (props: {
  categories: string[];
  excessText: string;
}) => {
  return props.categories.length ? (
    <TooltipHost content={<ContactCategoryText code={props.categories} />}>
      <Text>
        <ContactCategoryText code={props.categories[0]} />
        {props.excessText}
      </Text>
    </TooltipHost>
  ) : null;
};

const AdvancedSearchDialogInnerBase: FC<{
  onDismiss: () => void;
  onPassSelectionChange: (value?: string | undefined) => void;
}> = ({ onDismiss, onPassSelectionChange }) => {
  const theme = useTheme();

  const [pivotKey, setPivotKey] = useState<ContactType>(ContactType.Individual);

  const onRenderField = (
    props?: IDetailsColumnFieldProps,
    defaultRender?: DefaultRender
  ) => {
    if (props && defaultRender) {
      if (props.item.status === ContactStatus.Inactive) {
        return defaultRender({
          ...props,
          column: {
            ...props.column,
            className: mergeStyles({
              ...noWrap,
              fontStyle: "italic",
              display: "block"
            })
          }
        });
      } else {
        return defaultRender(props)!;
      }
    }
    return null;
  };

  const cols: IColumn[] = [
    {
      ...nameColumn(),
      onRenderField,
      onRender: (contact: Contact) => {
        const [firstDiscipline, ...rest] = contact.disciplines;

        const excessText = rest.length > 0 ? ` (+${rest.length})` : "";

        return (
          <Stack>
            <Text>{contact.name}</Text>
            <TooltipHost
              content={
                excessText ? (
                  <ContactCategoryText code={contact.disciplines} />
                ) : (
                  ""
                )
              }
            >
              <Text
                styles={{
                  root: {
                    color: theme?.palette.neutralTertiary,
                    fontSize: FontSizes.size12
                  }
                }}
              >
                <ContactCategoryText code={firstDiscipline} />
                {excessText}
              </Text>
            </TooltipHost>
          </Stack>
        );
      }
    },
    pivotKey === ContactType.Individual
      ? {
          name: "Organisations",
          key: "org",
          minWidth: 100,
          maxWidth: 200,
          onRenderField,
          onRender: (contact: Contact) => {
            return (
              <DataFetcher<Contact>
                fetch={({ practice }) =>
                  practice.getContact(contact.id, {
                    includeRelationships: true
                  })
                }
              >
                {({ employerContacts }) => {
                  const [firstEmployer, ...rest] = employerContacts;

                  const excessText =
                    rest.length > 0 ? ` (+${rest.length})` : "";

                  return (
                    <TooltipHost
                      content={
                        employerContacts.length > 0
                          ? employerContacts.map(x => x.name).join(", ")
                          : undefined
                      }
                    >
                      {firstEmployer && (
                        <Text>
                          {firstEmployer.name}
                          {excessText}
                        </Text>
                      )}
                    </TooltipHost>
                  );
                }}
              </DataFetcher>
            );
          }
        }
      : {
          ...categoryColumn(),
          onRenderField,
          onRender: (contact: Contact) => {
            const categories = contact.organisationCategories;
            const combinedArrLength = categories.slice(1).length;
            const excessText =
              combinedArrLength > 0 ? `  (+${combinedArrLength})` : "";

            return (
              <CategoriesCell categories={categories} excessText={excessText} />
            );
          }
        },
    {
      ...{
        name: "Contact method",
        key: "combinedCommunication",
        minWidth: 200,
        maxWidth: 250
      },
      onRenderField,
      onRender: (contact: Contact) => (
        <Stack>
          <span>
            <IndividualOrOrganisationPhoneColumn
              asPlainText
              contact={contact}
            />
          </span>
          <span>
            <IndividualAndOrganisationEmailColumn
              asPlainText
              contact={contact}
            />
          </span>
        </Stack>
      )
    },
    {
      ...phyAddressColumn(),
      onRenderField,
      onRender: (contact: Contact) => (
        <IndividualAndOrgAddressColumn asPlainText contact={contact} />
      )
    }
  ];

  return (
    <Pivot
      selectedKey={pivotKey}
      onLinkClick={(item: PivotItem) => {
        if (item.props.itemKey) {
          setPivotKey(item.props.itemKey as ContactType);
        }
      }}
      style={{ height: "100%" }}
      styles={{ itemContainer: { height: 370 } }}
    >
      <PivotItem
        headerText="Individuals"
        itemKey={ContactType.Individual}
        style={{ height: "100%" }}
      >
        <AdvancedSearchListFilter
          contactType={
            pivotKey as ContactType.Individual | ContactType.Organisation
          }
        >
          {({ values: filter }) => (
            <AdvancedContactSearchList
              columns={cols}
              filter={filter}
              onDismiss={onDismiss}
              onPassSelectionChange={onPassSelectionChange}
            />
          )}
        </AdvancedSearchListFilter>
      </PivotItem>
      <PivotItem
        headerText="Organisations"
        itemKey={ContactType.Organisation}
        style={{ height: "100%" }}
      >
        <AdvancedSearchListFilter
          contactType={
            pivotKey as ContactType.Individual | ContactType.Organisation
          }
        >
          {({ values: filter }) => (
            <AdvancedContactSearchList
              columns={cols}
              filter={filter}
              onDismiss={onDismiss}
              onPassSelectionChange={onPassSelectionChange}
            />
          )}
        </AdvancedSearchListFilter>
      </PivotItem>
    </Pivot>
  );
};

export const AdvancedSearchDialogInner = withFetch(
  x => [
    x.practice.ref.contactDisciplines.load(),
    x.practice.ref.organisationCategories.load()
  ],
  AdvancedSearchDialogInnerBase
);
